import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useParams } from 'react-router-dom'
import { CenteredCircularProgress, useExecPositionTypeLuts, useGlobalMessageHandler, useProtectedRoleHoldersLut } from 'components'
import { isExecPositionRowVacant } from 'helpers'
import { useSessionContext } from 'session'
import { formatErrorFieldMessages } from 'interfaces'
import {
  type BulkPositionChanges,
  type ExecutiveOrganization,
  bulkUpdatePositions,
  getExecutiveOrganization,
  getProtectedRoleHolders
} from 'services'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import {
  CCO_OPTIONS,
  CONGRESSIONAL_LIAISON_OPTIONS,
  EXEC_POS_TYPE_ELECTED,
  EXEC_POSITION_STATUSES,
  formatExecStatusLabel,
  getStatusesForOccupancy,
  isStateExecutiveRole
} from 'utils'

const personName = (position: ExecutivePosition): string => {
  // Exec position rows come from legacy data, and other flows treat person as
  // nullable (see isExecPositionRowVacant), so don't assume a person is present.
  const person = position.person
  if (person == null) return ''
  return `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim()
}

const statusLabel = (statusId: number): string => {
  const status = EXEC_POSITION_STATUSES[statusId]
  return status != null ? formatExecStatusLabel(status) : ''
}

export const ExecutiveOrgPositionsBulkPage: React.FC = () => {
  const { execOrgId } = useParams<{ execOrgId: string }>()
  const parsedOrgId = execOrgId != null ? parseInt(execOrgId, 10) : 0
  const { changeModuleName, openNewTab } = useSessionContext()
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const { setLutValues: setProtectedRoleHoldersLut } = useProtectedRoleHoldersLut()
  const { lutValues: execPosTypeLut } = useExecPositionTypeLuts()

  const [org, setOrg] = useState<ExecutiveOrganization | null>(null)
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [typeId, setTypeId] = useState<number | ''>('')
  const [statusId, setStatusId] = useState<number | ''>('')
  const [cco, setCco] = useState<number | ''>('')
  const [liaisonCode, setLiaisonCode] = useState<number | ''>('')
  const [title, setTitle] = useState('')

  useEffect(() => { changeModuleName('Bulk Edit Positions') }, [changeModuleName])

  const loadOrg = (): void => {
    if (Number.isNaN(parsedOrgId) || parsedOrgId === 0) {
      setLoading(false)
      return
    }
    setLoading(true)
    getExecutiveOrganization(parsedOrgId)
      .then(setOrg)
      .catch(() => { showErrorMessage('Failed to load organization positions.') })
      .finally(() => { setLoading(false) })
  }

  useEffect(loadOrg, [parsedOrgId])

  if (Number.isNaN(parsedOrgId) || parsedOrgId === 0) {
    return <Box>Invalid Executive Organization ID</Box>
  }
  if (loading) {
    return <CenteredCircularProgress loading={loading} size={100} position='absolute' />
  }
  if (org == null) {
    return <Box>Executive Organization not found.</Box>
  }

  const positions = org.positions.slice().sort((a, b) => a.displayOrder - b.displayOrder)
  const isStateOrg = isStateExecutiveRole(org.role.id)
  const allSelected = positions.length > 0 && positions.every(p => selectedIds.has(p.id))

  const toggleRow = (id: number): void => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = (): void => {
    setSelectedIds(allSelected ? new Set() : new Set(positions.map(p => p.id)))
  }

  const selectedPositions = positions.filter(p => selectedIds.has(p.id))
  const vacantSelected = selectedPositions.filter(p => isExecPositionRowVacant(p.person)).length
  const mixedOccupancy = vacantSelected > 0 && vacantSelected < selectedPositions.length
  const selectionIsVacant = selectedPositions.length > 0 && vacantSelected === selectedPositions.length
  const statusOptions = getStatusesForOccupancy(selectionIsVacant)
  // Narrowing the selection can strand an already-picked status outside the options.
  const statusStillOffered = statusOptions.some(([id]) => id === statusId)
  const effectiveStatusId = mixedOccupancy || !statusStillOffered ? '' : statusId

  const hasChanges =
    typeId !== '' || effectiveStatusId !== '' || cco !== '' || liaisonCode !== '' || title.trim() !== ''
  const canApply = selectedIds.size > 0 && hasChanges && !applying

  const handleApply = (): void => {
    const changes: BulkPositionChanges = {}
    if (typeId !== '') changes.typeId = Number(typeId)
    if (effectiveStatusId !== '') changes.statusId = Number(effectiveStatusId)
    if (cco !== '') changes.cco = Number(cco)
    if (liaisonCode !== '') changes.liaisonCode = Number(liaisonCode)
    if (title.trim() !== '') changes.title = title.trim()

    setApplying(true)
    bulkUpdatePositions(parsedOrgId, Array.from(selectedIds), changes)
      .then(response => {
        if (response.succeeded && response.result != null) {
          showSuccessMessage(`Updated ${response.result.updatedIds.length} position(s).`)
          setSelectedIds(new Set())
          setTypeId(''); setStatusId(''); setCco(''); setLiaisonCode(''); setTitle('')
          loadOrg()
          getProtectedRoleHolders().then(setProtectedRoleHoldersLut).catch(() => { showErrorMessage('Could not refresh the protected-role holders hint; it may be stale.') })
        } else {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to update positions.')
        }
      })
      .finally(() => { setApplying(false) })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
      <Stack spacing={2} width='98%'>
        <Typography variant='h5'>Bulk Edit Positions — {org.name}</Typography>

        <Card>
          <CardContent>
            <Typography variant='subtitle1' sx={{ mb: 1 }}>Apply to selected ({selectedIds.size})</Typography>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label='Title' value={title} onChange={e => { setTitle(e.target.value) }} disabled={applying} />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth disabled={applying}>
                  <InputLabel id='bulk-type'>Position Type</InputLabel>
                  <Select labelId='bulk-type' label='Position Type' value={typeId} onChange={e => { setTypeId(e.target.value === '' ? '' : Number(e.target.value)) }}>
                    <MenuItem value=''>— no change —</MenuItem>
                    {execPosTypeLut.execPosTypes.filter(t => t.id !== EXEC_POS_TYPE_ELECTED).map(t => <MenuItem key={t.id} value={t.id}>{t.desc}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth disabled={applying || mixedOccupancy}>
                  <InputLabel id='bulk-status'>Status</InputLabel>
                  <Select labelId='bulk-status' label='Status' value={effectiveStatusId} onChange={e => { setStatusId(e.target.value === '' ? '' : Number(e.target.value)) }}>
                    <MenuItem value=''>— no change —</MenuItem>
                    {statusOptions.map(([id, s]) => <MenuItem key={id} value={id}>{formatExecStatusLabel(s)}</MenuItem>)}
                  </Select>
                  {mixedOccupancy && <FormHelperText>Mixed occupancy — apply status to vacant and occupied positions separately.</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth disabled={applying}>
                  <InputLabel id='bulk-cco'>CCO</InputLabel>
                  <Select labelId='bulk-cco' label='CCO' value={cco} onChange={e => { setCco(e.target.value === '' ? '' : Number(e.target.value)) }}>
                    <MenuItem value=''>— no change —</MenuItem>
                    {CCO_OPTIONS.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {!isStateOrg && (
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth disabled={applying}>
                    <InputLabel id='bulk-liaison'>Cong. Liaison</InputLabel>
                    <Select labelId='bulk-liaison' label='Cong. Liaison' value={liaisonCode} onChange={e => { setLiaisonCode(e.target.value === '' ? '' : Number(e.target.value)) }}>
                      <MenuItem value=''>— no change —</MenuItem>
                      {CONGRESSIONAL_LIAISON_OPTIONS.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={1}>
                <Button fullWidth variant='contained' disabled={!canApply} onClick={handleApply}>
                  {applying ? <CircularProgress size={20} /> : 'Apply'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Paper>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <Checkbox checked={allSelected} indeterminate={selectedIds.size > 0 && !allSelected} onChange={toggleAll} />
                </TableCell>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {positions.length === 0
                ? <TableRow><TableCell colSpan={6}>No positions in this organization.</TableCell></TableRow>
                : positions.map((position, index) => (
                  <TableRow key={position.id} hover selected={selectedIds.has(position.id)}>
                    <TableCell padding='checkbox'>
                      <Checkbox checked={selectedIds.has(position.id)} onChange={() => { toggleRow(position.id) }} />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{personName(position)}</TableCell>
                    <TableCell>{position.title}</TableCell>
                    <TableCell>{statusLabel(position.statusId)}</TableCell>
                    <TableCell>
                      <IconButton size='small' aria-label='Open position' onClick={() => { openNewTab(`/us/executives/position-update/${position.id}`) }}>
                        <OpenInNewIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Paper>
      </Stack>
    </Box>
  )
}
